import { Link } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { PageHeader } from "@/components/data-display/page-header";
import { WipHint } from "@/components/ui/wip-hint";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input, Field } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipNotice, WipReason, WIP_CONTROL_PROPS, WipPanel } from "@/features/treasury/treasury-wip";

/**
 * STK-01 Create Staking Intent — designed layout, no backend in this
 * environment (design-port round 3, 2026-10-03).
 *
 * Ported from `.design-ref/prototype/features/treasury/treasury.tsx`
 * (`CreateStakingIntent`) with its sample form options, its estimated-summary
 * figures and its submit handler removed: the create-intent form's estimated
 * APY/commission/reward figures were previously fabricated here
 * (page-delivery-pipeline.md G6, 2026-09-23). The layout, card structure and
 * grid are the designer's; every value the backend would supply renders the
 * shared "Not available yet" marker and every control is disabled with the
 * reason attached. No endpoint exists for this domain, so this page makes no
 * call at all (see context/page_pipeline/BACKEND_GAPS.md).
 */

const DEFERRED_TITLE = "Creating a staking intent is not available in this environment yet";
const DEFERRED_DESCRIPTION =
  "Creating a staking intent requires policy evaluation and approval routing through the staking/approval backend, which does not exist in this environment yet.";

/** The estimated-summary rows the designer shows; none of them is sourceable here. */
const SUMMARY_ROWS = [
  "Asset",
  "Amount",
  "Estimated APY",
  "Provider",
  "Commission",
  "Activation Time",
  "Unbonding Period",
  "Est. Annual Reward",
];

export default function TreasuryStakingNewPage() {
  return (
    <div>
      <Breadcrumb
        items={[
          { label: "Treasury", href: "/treasury" },
          { label: "Staking Positions", href: "/treasury/staking" },
          { label: "Create Staking Intent" },
        ]}
      />
      <PageHeader title="Create Staking Intent" actions={<WipHint />} />
      <WipReason />
      <WipNotice title={DEFERRED_TITLE} description={DEFERRED_DESCRIPTION} />

      <div className="grid max-w-4xl grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-lg border border-line bg-surface p-6">
          <div className="space-y-5">
            <Field label="Asset" htmlFor="stk-asset">
              <Select id="stk-asset" className="w-full" {...WIP_CONTROL_PROPS}>
                <option>Not available yet</option>
              </Select>
            </Field>

            <Field label="Network" htmlFor="stk-network">
              <Select id="stk-network" className="w-full" {...WIP_CONTROL_PROPS}>
                <option>Not available yet</option>
              </Select>
            </Field>

            <Field label="Source Wallet" htmlFor="stk-wallet">
              <Select id="stk-wallet" className="w-full" {...WIP_CONTROL_PROPS}>
                <option>Not available yet</option>
              </Select>
            </Field>

            <Field label="Validator / Provider Route" htmlFor="stk-route">
              <Select id="stk-route" className="w-full" {...WIP_CONTROL_PROPS}>
                <option>Not available yet</option>
              </Select>
            </Field>

            <Field label="Amount" htmlFor="stk-amount">
              <Input id="stk-amount" inputMode="decimal" placeholder="Not available yet" {...WIP_CONTROL_PROPS} />
            </Field>

            <p className="text-xs text-muted">
              <Link to="/treasury/staking/validator" className="font-semibold text-primary hover:underline">
                View validator details
              </Link>
            </p>
          </div>
        </div>

        <div>
          <WipPanel title="Estimated Summary" className="bg-bg">
            <dl className="space-y-2 text-xs">
              {SUMMARY_ROWS.map((row) => (
                <div key={row} className="flex justify-between gap-3">
                  <dt className="text-muted">{row}</dt>
                  <dd className="text-right font-semibold text-ink">
                    <UnavailableValue />
                  </dd>
                </div>
              ))}
            </dl>
          </WipPanel>
          <div className="mt-3 rounded-lg border border-info-border bg-info-soft p-3 text-xs leading-relaxed text-info">
            Approval policy is evaluated by the staking and approval backend, which does not exist in this
            environment yet.
          </div>
        </div>
      </div>

      <div className="mt-6 flex max-w-4xl flex-wrap items-center justify-end gap-3">
        <Link to="/treasury/staking" className={buttonVariants({ variant: "secondary", size: "md" })}>
          Cancel
        </Link>
        <Button size="md" {...WIP_CONTROL_PROPS}>
          Submit Intent for Approval
        </Button>
      </div>
    </div>
  );
}
